import type { IAvailabilityChoice } from '@/domain/catalog/availability';
import type { TCatalogFilter } from '@/domain/catalog/catalog-filters';

/** What the Menú page is doing (plan §5.5). */
export type TCatalogMode = 'browse' | 'find' | 'reorder' | 'select';

/** The editor target (from the URL's `item` / `category`). */
export interface ICatalogEditorTarget {
  kind: 'item' | 'category';
  id: number | 'new';
  /** Create mode: the category of the new product. */
  categoryId: number | null;
  /** Create mode: the name typed in the search («Crear "{q}"»). */
  prefillName: string;
}

export type TCatalogPanelKind =
  | 'availability'
  | 'price'
  | 'rowMenu'
  | 'categoryMenu'
  | 'moreMenu';

/** The one floating panel of the page (anchored popover or phone sheet). */
export interface ICatalogBasePanel {
  kind: Exclude<TCatalogPanelKind, 'availability'>;
  /** The product or category it acts on; null for page-level panels. */
  targetId: number | null;
  anchor: HTMLElement | null;
  /** «Poner precio» of a product created hidden: a price also shows it. */
  reveal?: boolean;
}

/** «¿Dónde oculta…?» / «¿Dónde lo muestra?» (multi-branch stores). */
export interface ICatalogAvailabilityPanel {
  kind: 'availability';
  targetId: number;
  anchor: HTMLElement | null;
  entity: 'item' | 'category';
  turnOn: boolean;
  choices: IAvailabilityChoice[];
}

export type ICatalogPanel = ICatalogBasePanel | ICatalogAvailabilityPanel;

/** The Menú page's reactive UI state (records, not Sets: Vue 2 does not track Sets). */
export interface ICatalogPageState {
  menuId: number | null;
  filter: TCatalogFilter;
  /** The search field's text (the URL follows it debounced). */
  query: string;
  mode: TCatalogMode;
  /** Reorder mode opened from a category's ⋯: its products first. */
  reorderCategoryId: number | null;
  /** Reorder mode has an order not saved yet. */
  reorderDirty: boolean;
  selectedIds: Record<number, true>;
  /** Rows acted on stay visible while a filter or search no longer matches them. */
  pinnedIds: Record<number, true>;
  /** Rows the assistant points at («Ver en el menú»), for a moment. */
  highlightIds: Record<number, true>;
  /** The category the list is scrolled to (scroll-spy). */
  activeCategoryId: number | null;
  editor: ICatalogEditorTarget | null;
  editorDirty: boolean;
  /** The open editor's product or category name (for «¿Descartar…?»). */
  editorName: string;
  /** The open editor already saved a shared option group (they commit apart). */
  editorGroupsSaved: boolean;
  /** A category just created: its quick add takes the focus once. */
  focusQuickAddCategoryId: number | null;
  panel: ICatalogPanel | null;
}

export function createCatalogPageState(): ICatalogPageState {
  return {
    menuId: null,
    filter: 'all',
    query: '',
    mode: 'browse',
    reorderCategoryId: null,
    reorderDirty: false,
    selectedIds: {},
    pinnedIds: {},
    highlightIds: {},
    activeCategoryId: null,
    editor: null,
    editorDirty: false,
    editorName: '',
    editorGroupsSaved: false,
    focusQuickAddCategoryId: null,
    panel: null,
  };
}
